import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiUrl } from '../../config/api';
import './StrategyCreate.css';

const StrategyCreate: React.FC = () => {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState('');
  const [aiModel, setAiModel] = useState('gpt4');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [generatedStrategy, setGeneratedStrategy] = useState<any>(null);

  const handleGenerate = async () => {
    if (!prompt.trim()) return;

    setIsGenerating(true);
    try {
      // TODO: Call AI Strategy Service API
      const response = await fetch(getApiUrl('/api/strategies/generate'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          user_prompt: prompt,
          language: 'ru',
          ai_model: aiModel,
        }),
      });

      const data = await response.json();
      setGeneratedStrategy(data);
    } catch (error) {
      console.error('Error generating strategy:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleVoiceInput = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      alert('Voice input is not supported in this browser');
      return;
    }

    if (isRecording) {
      // Stop recording
      setIsRecording(false);
      // TODO: Stop MediaRecorder and send audio to API
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setIsRecording(true);

      // TODO: Implement actual voice recording with MediaRecorder
      // TODO: Send to Voice Service API
      // TODO: Set transcribed text to prompt

      console.log('Recording started', stream);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      alert('Failed to access microphone');
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>{t('strategy.create_with_ai')}</h1>
        <p>Describe your trading strategy in natural language</p>
      </div>

      <div className="create-grid">
        <div className="input-section card">
          <h2>{t('strategy.describe_strategy')}</h2>

          <div className="model-selector">
            <label>AI Model:</label>
            <select value={aiModel} onChange={(e) => setAiModel(e.target.value)}>
              <option value="gpt4">GPT-4 (OpenAI)</option>
              <option value="claude">Claude (Anthropic)</option>
              <option value="gemini">Gemini (Google)</option>
            </select>
          </div>

          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Example: Покупай BTC когда RSI меньше 30 и продавай когда RSI больше 70"
            rows={8}
            className="strategy-input"
          />

          <div className="input-actions">
            <button
              onClick={handleVoiceInput}
              className={`voice-button ${isRecording ? 'recording' : ''}`}
            >
              {isRecording ? '⏹ Stop Recording' : '🎤 ' + t('strategy.voice_input')}
            </button>

            <button
              onClick={handleGenerate}
              disabled={!prompt.trim() || isGenerating}
              className="primary generate-button"
            >
              {isGenerating ? t('common.loading') : t('strategy.generate')}
            </button>
          </div>

          {isGenerating && (
            <div className="loading-indicator">
              <div className="spinner"></div>
              <p>AI is generating your strategy...</p>
            </div>
          )}
        </div>

        {generatedStrategy && (
          <div className="output-section card">
            <h2>Generated Strategy</h2>

            <div className="strategy-preview">
              <h3>{generatedStrategy.name || 'Untitled Strategy'}</h3>
              <p>{generatedStrategy.description}</p>

              <div className="strategy-code">
                <h4>Strategy Logic (JSON DSL):</h4>
                <pre>{JSON.stringify(generatedStrategy.dsl, null, 2)}</pre>
              </div>

              {generatedStrategy.pine_script && (
                <div className="strategy-code">
                  <h4>Pine Script (TradingView):</h4>
                  <pre>{generatedStrategy.pine_script}</pre>
                </div>
              )}

              {generatedStrategy.python_code && (
                <div className="strategy-code">
                  <h4>Python Code (Backtrader):</h4>
                  <pre>{generatedStrategy.python_code}</pre>
                </div>
              )}

              <div className="strategy-actions">
                <button className="secondary">{t('strategy.refine')}</button>
                <button className="primary">{t('strategy.backtest')}</button>
                <button className="success">{t('common.save')}</button>
              </div>
            </div>
          </div>
        )}

        {!generatedStrategy && (
          <div className="placeholder-section card">
            <div className="placeholder-content">
              <h3>💡 Tips for creating strategies:</h3>
              <ul>
                <li>Be specific about entry and exit conditions</li>
                <li>Mention timeframes (1h, 4h, 1d, etc.)</li>
                <li>Specify indicators (RSI, MA, MACD, etc.)</li>
                <li>Include risk management rules</li>
              </ul>

              <h3>📝 Example prompts:</h3>
              <div className="example-prompts">
                <button onClick={() => setPrompt("Buy BTC when RSI is below 30 and sell when RSI is above 70")}>
                  RSI Strategy
                </button>
                <button onClick={() => setPrompt("Buy when 50 MA crosses above 200 MA, sell when it crosses below")}>
                  MA Crossover
                </button>
                <button onClick={() => setPrompt("Buy on breakout above 24h high with volume confirmation")}>
                  Breakout Strategy
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StrategyCreate;
